/*
 * PanamaJack — Available At (fecha de reposición por variante en la ficha de producto).
 *
 * QUÉ HACE
 *   Muestra "Disponible a partir de <fecha>" bajo el selector de variante cuando la
 *   variante seleccionada tiene una fecha de reposición (metafield stock.availableAt);
 *   vacía/oculta el aviso cuando la variante ya está disponible (sin fecha o "now").
 *
 * ORIGEN (6.0.0 — iso-funcional)
 *   Extraído de OptionManager.updateButtons en assets/theme.js (anclas:
 *   'cc-product-varmet-json', 'availableAtInfo', 'available_at', 'buscarTalla'):
 *     - varmet = JSON.parse(#cc-product-varmet-json-<productId>) → [{id, available_at}, ...]
 *     - al cambiar de variante: el available_at de la variante actual se pinta en
 *       .available-at-info como (dataset.prefix + available_at), salvo que sea "now".
 *     - Bug latente del 6.0.0: buscarTalla() podía devolver null y reventar en
 *       .classList.contains(...). Aquí se NULL-GUARDEA de forma estricta.
 *
 * MAPEO A 10.2.0 (markup ya rebasado a web components nativos)
 *   - Markup objetivo (sections/main-product.liquid):
 *       <div class="available-at-info" data-prefix="..." data-dynamic-variant-content="<block>-available-at">
 *       <script type="application/json" id="cc-product-varmet-json-<product.id>">[{id, available_at}, ...]</script>
 *   - Evento: 'on:variant:change' que dispara el <variant-picker> del vendor 10.2.0.
 *       detail.variant = objeto variante (o null/false si la selección está incompleta).
 *     Esto sustituye al antiguo guard buscarTalla().classList.contains('active'):
 *     en 10.2.0 solo hay variante resuelta (detail.variant con .id) cuando la talla
 *     seleccionada es real → equivalente iso-funcional a "la talla está activa".
 *   - Carrito AJAX nativo (<cart-form>/merge-list): no se toca aquí (este módulo solo
 *     actúa en la ficha de producto).
 *
 * FASE C
 *   Este archivo NO se cablea solo. En Fase C se enganchará desde layout/theme.liquid
 *   (p. ej. <script src="{{ 'pj-available-at.js' | asset_url }}" defer></script>),
 *   junto al resto de módulos pj-*. Mientras tanto el markup .available-at-info queda inerte.
 *
 * NOTAS
 *   - Vanilla JS, sin dependencias. Idempotente: se enlaza una sola vez por documento.
 *   - Null-guards estrictos en cada acceso al DOM y al JSON.
 */
(function () {
  "use strict";

  var BOUND_FLAG = "pjAvailableAtBound";

  // Caché de los arrays varmet por productId (parseados una sola vez).
  var varmetCache = {};

  /**
   * Lee y parsea el blob varmet de un producto.
   * @param {string|number} productId
   * @returns {Array<{id:number, available_at:string}>} array (vacío si falta o es inválido)
   */
  function getVarmet(productId) {
    if (productId == null) return [];
    var key = String(productId);
    if (Object.prototype.hasOwnProperty.call(varmetCache, key)) {
      return varmetCache[key];
    }

    var result = [];
    var el = document.getElementById("cc-product-varmet-json-" + key);
    if (el) {
      var raw = el.textContent || el.innerHTML || "";
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) result = parsed;
      } catch (err) {
        // JSON inválido: dejamos result = [] (iso-funcional: sin fecha no se muestra nada).
      }
    }

    varmetCache[key] = result;
    return result;
  }

  /**
   * Busca el available_at de una variante dentro del array varmet.
   * @param {Array} varmet
   * @param {number|string} variantId
   * @returns {string} la fecha (cadena) o "" si no hay
   */
  function findAvailableAt(varmet, variantId) {
    if (!Array.isArray(varmet) || variantId == null) return "";
    for (var i = 0; i < varmet.length; i++) {
      var entry = varmet[i];
      // Comparación laxa: el id del JSON es número, el de la variante también,
      // pero protegemos por si llega como string.
      if (entry && entry.id != null && String(entry.id) === String(variantId)) {
        return typeof entry.available_at === "string" ? entry.available_at : "";
      }
    }
    return "";
  }

  /**
   * Resuelve el productId a partir del evento o del DOM contenedor.
   * @param {Event} evt - evento on:variant:change
   * @param {Element} infoEl - el .available-at-info dentro del .product-area
   * @returns {string|null}
   */
  function resolveProductId(evt, infoEl) {
    // 1) Desde el detail.form (input hidden o data-product-id) si existe.
    var detail = evt && evt.detail;
    if (detail && detail.form) {
      var form = detail.form;
      if (form.dataset && form.dataset.productId) return form.dataset.productId;
      var hiddenPid = form.querySelector
        ? form.querySelector('[name="product-id"], [data-product-id]')
        : null;
      if (hiddenPid) {
        return hiddenPid.value || hiddenPid.getAttribute("data-product-id");
      }
    }

    // 2) Fallback: único blob varmet presente en el área de producto / documento.
    var area = infoEl && infoEl.closest ? infoEl.closest(".product-area") : null;
    var scope = area || document;
    var blob = scope.querySelector('script[id^="cc-product-varmet-json-"]');
    if (blob && blob.id) {
      return blob.id.replace("cc-product-varmet-json-", "");
    }
    return null;
  }

  /**
   * Aplica el available_at al .available-at-info correspondiente.
   * @param {Event} evt
   */
  function handleVariantChange(evt) {
    // Localizamos el aviso dentro del mismo product-area que disparó el evento.
    var area =
      evt && evt.target && evt.target.closest
        ? evt.target.closest(".product-area")
        : null;
    var scope = area || document;
    var infoEl = scope.querySelector(".available-at-info");
    if (!infoEl) return; // NULL-GUARD: nada que rellenar.

    var detail = evt && evt.detail;
    var variant = detail ? detail.variant : null;

    // Sin variante resuelta (selección incompleta) → limpiar/ocultar.
    // (Equivalente iso-funcional al guard buscarTalla().classList.contains('active')
    //  del 6.0.0, sin el null-deref que lo reventaba.)
    if (!variant || variant.id == null) {
      clearInfo(infoEl);
      return;
    }

    var productId = resolveProductId(evt, infoEl);
    var varmet = getVarmet(productId);
    var availableAt = findAvailableAt(varmet, variant.id);

    // Sin fecha o "now" → disponible ya: limpiar/ocultar.
    if (!availableAt || availableAt.toLowerCase() === "now") {
      clearInfo(infoEl);
      return;
    }

    var prefix =
      (infoEl.dataset && infoEl.dataset.prefix) ||
      infoEl.getAttribute("data-prefix") ||
      "";
    infoEl.innerText = prefix + availableAt;
    infoEl.style.display = "block";
  }

  /**
   * Vacía y oculta el aviso (NULL-GUARD: infoEl ya validado por el llamante).
   * @param {Element} infoEl
   */
  function clearInfo(infoEl) {
    infoEl.innerText = "";
    infoEl.style.display = "none";
  }

  function init() {
    // Idempotencia: enlazar una sola vez por documento.
    if (document.documentElement.dataset[BOUND_FLAG]) return;
    document.documentElement.dataset[BOUND_FLAG] = "1";

    // El evento del <variant-picker> burbujea → escuchamos en document.
    document.addEventListener("on:variant:change", handleVariantChange);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
